import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userApi, authApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
} from 'components/icons';
import TagInput from '../../components/ui/TagInput';
import NaicsCodePicker from '../../components/ui/NaicsCodePicker';
import { SET_ASIDE_TYPES, INDUSTRIES } from '../../data/govconData';
import PageHeader from '../../components/ui/PageHeader';
import ApiKeysPanel from '../../components/settings/ApiKeysPanel';

const Settings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  // Integrations links here with ?tab=api-keys. Without this the link would
  // land on Profile, which is the same broken promise as a button that does
  // nothing — just quieter.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['profile', 'company', 'notifications', 'security', 'api-keys'];
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    requestedTab && VALID_TABS.includes(requestedTab) ? requestedTab : 'profile',
  );

  const selectTab = (tab: string) => {
    setActiveTab(tab);
    // Keep the URL honest so the tab survives a refresh or a shared link.
    setSearchParams(tab === 'profile' ? {} : { tab }, { replace: true });
  };
  const [isLoading, setIsLoading] = useState(false);

  // Company tag fields (managed state for TagInput components)
  const [naicsTags, setNaicsTags] = useState<string[]>(user?.naicsCodes || []);
  const [certTags, setCertTags] = useState<string[]>((user as any)?.certifications || []);
  const [capabilityTags, setCapabilityTags] = useState<string[]>((user as any)?.capabilities || []);
  const [contractorType, setContractorType] = useState<string>((user as any)?.contractorType || '');
  // Fetched separately from the profile: the logo is a base64 data URL and does
  // not belong on an endpoint the app calls at boot.
  const [logo, setLogo] = useState<string | null>(null);

  // These useState initialisers only run on first render. If the user object is
  // still loading then, the tag fields stay empty — and because submit always
  // sends them, saving would wipe the stored values. Re-sync when user arrives.
  useEffect(() => {
    if (!user) return;
    setNaicsTags(user.naicsCodes || []);
    setCertTags((user as any).certifications || []);
    setCapabilityTags((user as any).capabilities || []);
    setContractorType((user as any).contractorType || '');
  }, [user]);

  // 2FA state
  useEffect(() => {
    if (activeTab !== 'company') return;
    userApi.getLogo().then(r => setLogo(r.data.logo)).catch(() => setLogo(null));
  }, [activeTab]);

  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Notification prefs
  const notifPrefs = (user as any)?.notificationPrefs || {
    email_alerts: true,
    deadline_reminders: true,
    team_updates: true,
    marketing: false,
  };

  // The text fields in both forms are uncontrolled (defaultValue), which React
  // reads once, when the input mounts. If the user object is replaced after
  // that — login returns a trimmed user, refreshUser brings the full profile —
  // the fields keep showing the first, emptier values while the rest of the
  // app cites a populated profile. Remount the forms whenever a displayed
  // value changes. Keying on the displayed values, not the whole object, means
  // an unrelated refresh such as a logo upload does not wipe a draft edit.
  const formKey = [
    user?.id, user?.firstName, user?.lastName, user?.phone, user?.companyName,
    user?.companySize, user?.industry, user?.website, user?.cageCode, user?.ueiSam,
    user?.companyCity, user?.companyState, user?.companyZip,
  ].map((v) => v ?? '').join('|');

  const handleProfileUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const data: Record<string, any> = Object.fromEntries(formData.entries());

    // Use tag state for NAICS, certifications and capabilities (overrides hidden form inputs)
    data.naicsCodes = naicsTags;
    data.certifications = certTags;
    data.capabilities = capabilityTags;
    data.contractorType = contractorType || null;
    // Clean up hidden input values that may have been submitted
    delete data._naicsCodes;
    delete data._certifications;
    delete data._capabilities;

    try {
      await userApi.updateMe(data);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await userApi.changePassword({ currentPassword, newPassword });
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setTwoFALoading(true);
    try {
      const response = await authApi.enable2FA();
      const data = response.data;
      setQrCodeUrl(data.qrCodeUrl || data.data?.qrCodeUrl);
      setTotpSecret(data.secret || data.data?.secret);
      setShow2FASetup(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleConfirm2FA = async () => {
    if (totpCode.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }
    setTwoFALoading(true);
    try {
      await authApi.confirm2FA({ code: totpCode });
      await refreshUser();
      setShow2FASetup(false);
      setTotpCode('');
      toast.success('Two-factor authentication enabled');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Invalid code — try again');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error('Enter your password to disable 2FA');
      return;
    }
    setTwoFALoading(true);
    try {
      await authApi.disable2FA({ password: disablePassword });
      await refreshUser();
      setDisablePassword('');
      toast.success('Two-factor authentication disabled');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleNotifChange = async (key: string, value: boolean) => {
    const updated = { ...notifPrefs, [key]: value };
    try {
      await userApi.updateMe({ notificationPrefs: updated });
      await refreshUser();
    } catch {
      toast.error('Failed to save preference');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        subtitle="Manage your account settings and preferences"
      />

      {/* API keys sit next to password and 2FA rather than under Integrations:
          a key is a credential, and credentials belong where someone already
          goes to change a password or revoke a session. Integrations links
          here rather than duplicating it. */}
      <div className="flex space-x-1 rounded-xl bg-surface-sunk p-1 max-w-2xl overflow-x-auto">
        {[
          { id: 'profile', label: 'Profile' },
          { id: 'company', label: 'Company' },
          { id: 'notifications', label: 'Notifications' },
          { id: 'security', label: 'Security' },
          { id: 'api-keys', label: 'Agent Access' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => selectTab(tab.id)}
            className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white text-brand-700 shadow'
                : 'text-content-muted hover:bg-surface/[0.5] hover:text-content'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="bg-surface shadow rounded-lg p-6">
          <ApiKeysPanel />
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-surface shadow rounded-lg p-6">
          <form key={formKey} onSubmit={handleProfileUpdate} className="space-y-6 max-w-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input name="firstName" type="text" defaultValue={user?.firstName} className="input" />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input name="lastName" type="text" defaultValue={user?.lastName} className="input" />
              </div>
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" value={user?.email} disabled className="input bg-surface-sunk" />
              <p className="mt-1 text-xs text-content-muted">Email cannot be changed</p>
            </div>
            <div>
              <label className="label">Phone</label>
              <input name="phone" type="tel" defaultValue={user?.phone} className="input" />
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {/* Company Tab */}
      {activeTab === 'company' && (
        <div className="bg-surface shadow rounded-lg p-6">
          <form key={formKey} onSubmit={handleProfileUpdate} className="space-y-6 max-w-lg">
            <div>
              <label className="label">Company Logo</label>
              <p className="text-xs text-content-muted mb-1">
                Used on proposal cover pages. Best at roughly 2.5:1 — wider than it is tall —
                because the cover places it at a fixed size and does not preserve other shapes.
              </p>

              {/* Showing the current logo is the whole point: without it there
                  is no way to tell an account that has one from an account that
                  does not, and the answer to "why is it still asking me" is
                  invisible. */}
              {logo && (
                <div className="mb-3 flex items-center gap-4">
                  <div className="rounded-md border border-border bg-white p-2">
                    <img src={logo} alt="Current company logo" className="h-12 w-auto object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Remove your company logo? Proposal cover pages will no longer show it.')) return;
                      try {
                        await userApi.removeLogo();
                        setLogo(null);
                        await refreshUser();
                        toast.success('Logo removed');
                      } catch {
                        toast.error('Could not remove the logo');
                      }
                    }}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              )}

              <input
                name="companyLogo"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) {
                    toast.error('Logo must be under 2MB');
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      await userApi.updateMe({ companyLogo: reader.result as string });
                      setLogo(reader.result as string);
                      await refreshUser();
                      toast.success('Logo uploaded');
                    } catch {
                      toast.error('Failed to upload logo');
                    }
                  };
                  reader.readAsDataURL(file);
                }}
                className="input text-sm file:mr-3 file:py-1 file:px-3 file:border-0 file:text-sm file:bg-brand-50 file:text-brand-700 file:rounded-md file:cursor-pointer"
              />
            </div>
            <div>
              <label className="label">Company Name</label>
              <input name="companyName" type="text" defaultValue={user?.companyName} className="input" />
            </div>
            <div>
              <label className="label">How do you usually pursue work?</label>
              <select
                name="_contractorType"
                value={contractorType}
                onChange={(e) => setContractorType(e.target.value)}
                className="input"
              >
                <option value="">Select...</option>
                <option value="direct_provider">
                  We perform the work ourselves
                </option>
                <option value="generalist_contractor">
                  We bid across categories and bring in subs or partners
                </option>
                <option value="both">Both, depending on the opportunity</option>
              </select>
              <p className="mt-1 text-xs text-content-muted">
                This sets your starting point only — you'll still choose how to pursue each
                individual opportunity when you write a proposal.
              </p>
            </div>

            <NaicsCodePicker
              label={contractorType === 'generalist_contractor' ? 'NAICS Codes (optional)' : 'NAICS Codes'}
              helpText={
                contractorType === 'generalist_contractor'
                  ? "Optional for generalists. If no code really describes what you bid on, leave this empty and use Capabilities below — a guessed code will skew your matches toward work you don't want."
                  : "Search or browse by category to add your company's NAICS codes"
              }
              value={naicsTags}
              onChange={setNaicsTags}
            />

            <TagInput
              name="_capabilities"
              label="Capabilities"
              helpText="Plain language lines of work — e.g. janitorial, grounds maintenance, facilities O&M. Used to match opportunities when NAICS codes don't describe you well."
              placeholder="Add a capability..."
              value={capabilityTags}
              onChange={setCapabilityTags}
            />

            {contractorType === 'generalist_contractor' && capabilityTags.length === 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm text-amber-800">
                  Add a few capabilities so we have something to match on. Without either NAICS
                  codes or capabilities, your match scores reflect how good a contract is in
                  general — not how well it fits you.
                </p>
              </div>
            )}
            <div>
              <label className="label">Company Size</label>
              <select name="companySize" defaultValue={user?.companySize || ''} className="input">
                <option value="">Select size...</option>
                <option value="SOLO">Solo / 1 person</option>
                <option value="SMALL">Small (2-50 employees)</option>
                <option value="MEDIUM">Medium (51-500 employees)</option>
                <option value="LARGE">Large (501-5000 employees)</option>
                <option value="ENTERPRISE">Enterprise (5000+)</option>
              </select>
            </div>
            <div>
              <label className="label">Industry</label>
              <select name="industry" defaultValue={user?.industry || ''} className="input">
                <option value="">Select industry...</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Website</label>
              <input name="website" type="url" defaultValue={user?.website} className="input" />
            </div>
            <div>
              <label className="label">CAGE Code</label>
              <input name="cageCode" type="text" defaultValue={user?.cageCode} className="input" />
            </div>
            <div>
              <label className="label">UEI (SAM)</label>
              <input name="ueiSam" type="text" defaultValue={user?.ueiSam} className="input" />
            </div>
            <TagInput
              name="_certifications"
              label="Set-aside certifications"
              helpText="8(a), SDVOSB, WOSB, EDWOSB, HUBZone, VOSB — or add 'None of these'. Most federal solicitations are set aside for a category of small business, so this decides which ones you are eligible to bid."
              value={certTags}
              onChange={setCertTags}
              suggestions={SET_ASIDE_TYPES.map(s => ({ value: s.code, label: s.label }))}
              placeholder="Search certifications..."
            />
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">City</label>
                <input name="companyCity" type="text" defaultValue={user?.companyCity} className="input" />
              </div>
              <div>
                <label className="label">State</label>
                <input name="companyState" type="text" defaultValue={user?.companyState} className="input" placeholder="e.g. VA" maxLength={2} />
              </div>
              <div>
                <label className="label">ZIP Code</label>
                <input name="companyZip" type="text" defaultValue={user?.companyZip} className="input" />
              </div>
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-surface shadow rounded-lg p-6">
          <h3 className="text-title text-content mb-4">Notification Preferences</h3>
          <div className="space-y-4 max-w-lg">
            {[
              { id: 'email_alerts', label: 'Email Alerts', desc: 'Get notified about new opportunities matching your profile' },
              { id: 'deadline_reminders', label: 'Deadline Reminders', desc: 'Receive reminders before proposal deadlines' },
              { id: 'team_updates', label: 'Team Updates', desc: 'Notifications about team member activities' },
              { id: 'marketing', label: 'Marketing Emails', desc: 'Product updates and GovCon tips' },
            ].map((item) => (
              <div key={item.id} className="flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id={item.id}
                    type="checkbox"
                    checked={notifPrefs[item.id] !== false}
                    onChange={(e) => handleNotifChange(item.id, e.target.checked)}
                    className="h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor={item.id} className="font-medium text-content">{item.label}</label>
                  <p className="text-content-muted">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6 max-w-lg">
          {/* Two-Factor Authentication */}
          <div className="bg-surface shadow rounded-lg p-6">
            <h3 className="text-title text-content mb-1">Two-Factor Authentication</h3>
            <p className="text-sm text-content-muted mb-4">Add an extra layer of security to your account</p>

            {/* Current 2FA status */}
            <div className="space-y-4">
              {/* Email OTP — always active */}
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <EnvelopeIcon className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-content">Email OTP</p>
                    <p className="text-xs text-content-muted">Code sent to your email on login</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
              </div>

              {/* Authenticator App */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${user?.twoFactorEnabled ? 'bg-green-50 border-green-200' : 'bg-surface-sunk border-border'}`}>
                <div className="flex items-center gap-3">
                  <ShieldCheckIcon className={`h-5 w-5 ${user?.twoFactorEnabled ? 'text-green-600' : 'text-content-subtle'}`} />
                  <div>
                    <p className="text-sm font-medium text-content">Authenticator App</p>
                    <p className="text-xs text-content-muted">Google Authenticator, Authy, etc.</p>
                  </div>
                </div>
                {user?.twoFactorEnabled ? (
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                ) : (
                  <button
                    onClick={handleEnable2FA}
                    disabled={twoFALoading}
                    className="text-xs px-3 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {twoFALoading ? 'Loading...' : 'Enable'}
                  </button>
                )}
              </div>

              {/* SMS — Coming Soon */}
              <div className="flex items-center justify-between p-3 bg-surface-sunk border border-border rounded-lg opacity-60">
                <div className="flex items-center gap-3">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-content-subtle" />
                  <div>
                    <p className="text-sm font-medium text-content">SMS Text Message</p>
                    <p className="text-xs text-content-muted">Verification code via text message</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-surface-sunk text-content-muted font-medium">Coming Soon</span>
              </div>
            </div>

            {/* 2FA Setup Flow */}
            {show2FASetup && !user?.twoFactorEnabled && (
              <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
                <h4 className="font-medium text-content mb-3">Set Up Authenticator App</h4>
                <ol className="text-sm text-content-muted space-y-2 mb-4">
                  <li>1. Open your authenticator app (Google Authenticator, Authy, etc.)</li>
                  <li>2. Scan the QR code below or enter the secret key manually</li>
                  <li>3. Enter the 6-digit code from the app to verify</li>
                </ol>

                {qrCodeUrl && (
                  <div className="text-center mb-4">
                    <img src={qrCodeUrl} alt="QR Code" className="mx-auto w-48 h-48 border rounded-lg" />
                    <p className="text-xs text-content-muted mt-2">Or enter manually: <code className="bg-surface-sunk px-2 py-0.5 rounded text-xs">{totpSecret}</code></p>
                  </div>
                )}

                <div className="flex gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="input flex-1 text-center text-lg tracking-widest"
                    maxLength={6}
                  />
                  <button
                    onClick={handleConfirm2FA}
                    disabled={twoFALoading || totpCode.length !== 6}
                    className="btn-primary disabled:opacity-50"
                  >
                    {twoFALoading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
                <button onClick={() => setShow2FASetup(false)} className="mt-2 text-xs text-content-muted hover:text-content">
                  Cancel
                </button>
              </div>
            )}

            {/* Disable 2FA */}
            {user?.twoFactorEnabled && (
              <div className="mt-4 p-4 bg-surface-sunk border border-border rounded-lg">
                <p className="text-sm text-content-muted mb-3">Enter your password to disable authenticator app 2FA:</p>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Your password"
                    className="input flex-1"
                  />
                  <button
                    onClick={handleDisable2FA}
                    disabled={twoFALoading || !disablePassword}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                  >
                    {twoFALoading ? '...' : 'Disable'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="bg-surface shadow rounded-lg p-6">
            <h3 className="text-title text-content mb-1">Change Password</h3>
            <p className="text-sm text-content-muted mb-4">Update your password regularly to keep your account secure</p>
            <div className="space-y-4">
              <div>
                <label className="label">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="label">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="Confirm new password"
                />
              </div>
              <button
                onClick={handlePasswordChange}
                disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                className="btn-primary disabled:opacity-50"
              >
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>

          {/* Active Sessions — placeholder for future */}
          <div className="bg-surface shadow rounded-lg p-6 opacity-60">
            <h3 className="text-title text-content mb-1">Active Sessions</h3>
            <p className="text-sm text-content-muted">Manage devices where you're currently logged in</p>
            <p className="text-xs text-content-subtle mt-3">Coming soon</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
